<template>
  <ion-page>
    <ion-header class="ion-no-border events-page-header" :class="{ 'page-header--scrolled': headerScrolled }">
      <ion-toolbar>
        <ion-buttons slot="start">
          <ion-back-button text="" :default-href="backHref"></ion-back-button>
        </ion-buttons>
        <ion-title>{{ isNew ? 'Новое событие' : 'Редактирование' }}</ion-title>
      </ion-toolbar>
    </ion-header>
    <ion-content class="main-background events-content" :fullscreen="true" @ionScroll="onContentScroll">
      <form class="ion-padding event-edit-form" @submit.prevent="save">
        <ion-item>
          <ion-label position="stacked">Название *</ion-label>
          <ion-input v-model="form.title" aria-label="Название события" required></ion-input>
        </ion-item>
        <ion-item>
          <ion-label position="stacked">Описание</ion-label>
          <ion-textarea v-model="form.description" aria-label="Описание" :rows="4"></ion-textarea>
        </ion-item>
        <ion-item>
          <ion-label position="stacked">Место проведения</ion-label>
          <ion-input v-model="form.location" aria-label="Место проведения"></ion-input>
        </ion-item>
        <ion-item>
          <ion-label position="stacked">Дата и время начала *</ion-label>
          <ion-datetime-button
            id="start-datetime-btn"
            datetime="start-datetime"
            class="datetime-trigger"
            @click="openStartDatetime = true"
          ></ion-datetime-button>
        </ion-item>
        <ion-modal class="datetime-modal" keep-contents-mounted :is-open="openStartDatetime" @didDismiss="openStartDatetime = false">
          <ion-datetime
            id="start-datetime"
            presentation="date-time"
            locale="ru-RU"
            :prefer-wheel="false"
            :value="form.startAtInput || todayIso"
            show-default-buttons
            done-text="Готово"
            cancel-text="Отмена"
            @ionChange="onStartDatetimeChange($event)"
            @ionCancel="openStartDatetime = false"
          >
          </ion-datetime>
        </ion-modal>

        <ion-item>
          <ion-label position="stacked">Дата и время окончания</ion-label>
          <ion-datetime-button
            id="end-datetime-btn"
            datetime="end-datetime"
            class="datetime-trigger"
            @click="openEndDatetime = true"
          ></ion-datetime-button>
        </ion-item>
        <ion-modal class="datetime-modal" keep-contents-mounted :is-open="openEndDatetime" @didDismiss="openEndDatetime = false">
          <ion-datetime
            id="end-datetime"
            presentation="date-time"
            locale="ru-RU"
            :prefer-wheel="false"
            :value="form.endAtInput || todayIso"
            show-default-buttons
            done-text="Готово"
            cancel-text="Отмена"
            @ionChange="onEndDatetimeChange($event)"
            @ionCancel="openEndDatetime = false"
          >
          </ion-datetime>
        </ion-modal>
        <ion-item>
          <ion-toggle v-model="form.isPaid" aria-label="Платное событие">Платное событие</ion-toggle>
        </ion-item>
        <ion-item>
          <ion-toggle v-model="form.published" aria-label="Опубликовано">Опубликовано (видно в списке событий)</ion-toggle>
        </ion-item>
        <ion-item>
          <ion-label position="stacked">Лимит регистраций (пусто — без лимита)</ion-label>
          <ion-input
            type="number"
            v-model.number="form.maxRegistrations"
            aria-label="Лимит регистраций"
            min="1"
            placeholder="Не ограничено"
          ></ion-input>
        </ion-item>
        <div v-if="form.isPaid" class="tiers-section">
          <h3>Тарифы</h3>
          <div v-for="(t, i) in form.priceTiers" :key="i" class="tier-row">
            <ion-input v-model="t.name" class="tier-input-name" aria-label="Название тарифа" placeholder="Название тарифа"></ion-input>
            <ion-input type="number" v-model.number="t.amountRub" class="tier-input-amount" aria-label="Стоимость, руб" placeholder="₽" min="0"></ion-input>
            <ion-button fill="clear" size="small" class="tier-row-remove" @click="removeTier(i)">×</ion-button>
          </div>
          <ion-button fill="outline" size="small" class="tier-add-btn" @click="addTier">+ Тариф</ion-button>
        </div>
        <ion-item v-if="!isNew && eventId">
          <ion-label>Логотип</ion-label>
          <input type="file" accept="image/*" @change="onLogoChange" ref="logoInput" style="display:none" />
          <ion-button fill="outline" class="logo-upload-btn" @click="openLogoPicker">Загрузить</ion-button>
          <img v-if="logoPreview" :src="logoPreview" class="logo-preview" alt="" />
        </ion-item>
        <ion-button expand="block" type="submit" :disabled="saving" class="save-event-btn ion-margin-top">Сохранить</ion-button>
        <ion-button v-if="!isNew" expand="block" color="danger" fill="outline" :disabled="saving" @click="remove" class="ion-margin-top">Удалить событие</ion-button>
      </form>
    </ion-content>
  </ion-page>
</template>

<script lang="ts">
import {
  alertController,
  IonBackButton,
  IonButton,
  IonButtons,
  IonContent,
  IonDatetime,
  IonDatetimeButton,
  IonHeader,
  IonInput,
  IonItem,
  IonLabel,
  IonModal,
  IonPage,
  IonTextarea,
  IonTitle,
  IonToggle,
  IonToolbar
} from '@ionic/vue';
import { defineComponent } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import eventsService from '@/services/events.service';
import dayjs from 'dayjs';

export default defineComponent({
  name: 'AdminEventEdit',
  components: {
    IonBackButton,
    IonButton,
    IonButtons,
    IonContent,
    IonDatetime,
    IonDatetimeButton,
    IonHeader,
    IonInput,
    IonItem,
    IonLabel,
    IonModal,
    IonPage,
    IonTextarea,
    IonTitle,
    IonToggle,
    IonToolbar
  },
  setup() {
    const route = useRoute();
    const router = useRouter();
    return { route, router };
  },
  data() {
    return {
      eventsService,
      eventId: null as number | null,
      saving: false,
      headerScrolled: false,
      openStartDatetime: false,
      openEndDatetime: false,
      logoFile: null as File | null,
      logoPreview: '',
      /** Текущий путь логотипа с сервера — передаём при update, чтобы не затирать при сохранении без новой загрузки */
      existingLogoPath: null as string | null,
      form: {
        title: '',
        description: '',
        location: '',
        startAtInput: '',
        endAtInput: '',
        isPaid: false,
        published: false,
        maxRegistrations: null as number | null,
        priceTiers: [] as { name: string; amountRub: number }[]
      }
    };
  },
  computed: {
    isNew(): boolean {
      return this.route.name === 'AdminEventNew';
    },
    backHref(): string {
      return this.eventId ? `/admin-events/${this.eventId}/registrations` : '/admin-events';
    },
    todayIso(): string {
      return dayjs().format('YYYY-MM-DDTHH:mm');
    }
  },
  async mounted() {
    const id = Number(this.route.params.id);
    if (id && !this.isNew) {
      this.eventId = id;
      const ev = await eventsService.getEvent(id);
      if (ev) {
        this.form.title = ev.title;
        this.form.description = ev.description || '';
        this.form.location = ev.location || '';
        this.form.startAtInput = ev.startAt ? dayjs(ev.startAt).format('YYYY-MM-DDTHH:mm') : '';
        this.form.endAtInput = ev.endAt ? dayjs(ev.endAt).format('YYYY-MM-DDTHH:mm') : '';
        this.form.isPaid = ev.isPaid;
        this.form.published = ev.published !== false;
        this.form.maxRegistrations = ev.maxRegistrations ?? null;
        this.form.priceTiers = (ev.priceTiers || []).map(t => ({
          name: t.name,
          amountRub: t.amountKopecks / 100
        }));
        if (ev.priceTiers?.length === 0 && ev.isPaid) {
          this.form.priceTiers = [{ name: 'Участие', amountRub: 0 }];
        }
        if (ev.logoPath) {
          this.logoPreview = eventsService.logoUrl(ev.logoPath);
        }
      }
    } else if (this.isNew && this.form.priceTiers.length === 0) {
      this.form.priceTiers = [{ name: 'Участие', amountRub: 0 }];
    }
  },
  methods: {
    onContentScroll(e: CustomEvent) {
      const scrollTop = e.detail?.scrollTop ?? 0;
      this.headerScrolled = scrollTop > 16;
    },
    addTier() {
      this.form.priceTiers.push({ name: '', amountRub: 0 });
    },
    removeTier(i: number) {
      this.form.priceTiers.splice(i, 1);
    },
    formatDateTimeDisplay(value: string): string {
      if (!value) return '';
      return dayjs(value).locale('ru').format('D MMMM YYYY, HH:mm');
    },
    onStartDatetimeChange(e: CustomEvent) {
      const v = (e as any).detail?.value;
      if (v) {
        this.form.startAtInput = v;
        this.form.endAtInput = dayjs(v).add(1, 'hour').format('YYYY-MM-DDTHH:mm');
      }
      this.openStartDatetime = false;
    },
    onEndDatetimeChange(e: CustomEvent) {
      const v = (e as any).detail?.value;
      if (v) this.form.endAtInput = v;
      this.openEndDatetime = false;
    },
    openLogoPicker() {
      const input = this.$refs.logoInput as HTMLInputElement | undefined;
      input?.click();
    },
    onLogoChange(e: Event) {
      const input = e.target as HTMLInputElement;
      const file = input.files?.[0];
      if (file) {
        this.logoFile = file;
        this.logoPreview = URL.createObjectURL(file);
      }
      input.value = '';
    },
    async save() {
      this.saving = true;
      try {
        const startAt = this.form.startAtInput ? new Date(this.form.startAtInput).toISOString() : '';
        const endAt = this.form.endAtInput ? new Date(this.form.endAtInput).toISOString() : null;
        const payload = {
          title: this.form.title,
          description: this.form.description || null,
          location: this.form.location || null,
          startAt,
          endAt,
          isPaid: this.form.isPaid,
          priceTiers: this.form.priceTiers
            .filter(t => t.name.trim())
            .map(t => ({ name: t.name.trim(), amountKopecks: Math.round(t.amountRub * 100) }))
        };
        if (this.isNew) {
          const created = await eventsService.createEvent({
            title: payload.title,
            description: payload.description || undefined,
            location: payload.location || undefined,
            startAt: payload.startAt,
            endAt: payload.endAt || undefined,
            isPaid: payload.isPaid,
            published: this.form.published,
            maxRegistrations: this.form.maxRegistrations && this.form.maxRegistrations > 0 ? this.form.maxRegistrations : undefined
          });
          if (payload.priceTiers.length) {
            await eventsService.setPriceTiers(created.id, payload.priceTiers);
          }
          this.eventId = created.id;
          if (this.logoFile) {
            await eventsService.uploadLogo(created.id, this.logoFile);
          }
          this.router.replace(`/admin-events/${created.id}/registrations`);
        } else if (this.eventId) {
          const updatePayload: Parameters<typeof eventsService.updateEvent>[1] = {
            title: payload.title,
            description: payload.description ?? undefined,
            location: payload.location ?? undefined,
            startAt: payload.startAt as any,
            endAt: payload.endAt as any,
            isPaid: payload.isPaid,
            published: this.form.published,
            maxRegistrations:
              this.form.maxRegistrations != null && this.form.maxRegistrations > 0 ? this.form.maxRegistrations : null
          };
          if (!this.logoFile && this.existingLogoPath) {
            updatePayload.logoPath = this.existingLogoPath;
          }
          await eventsService.updateEvent(this.eventId, updatePayload);
          await eventsService.setPriceTiers(this.eventId, payload.priceTiers);
          if (this.logoFile) {
            await eventsService.uploadLogo(this.eventId, this.logoFile);
          }
          this.router.replace(`/admin-events/${this.eventId}/registrations`);
        }
      } catch (e) {
        console.error(e);
      }
      this.saving = false;
    },
    async remove() {
      const id = this.eventId;
      if (id == null) return;
      const alert = await alertController.create({
        cssClass: 'liga-delete-event-alert',
        header: 'Удаление события',
        message: 'Удалить это событие? Действие нельзя отменить.',
        buttons: [
          {
            text: 'Отмена',
            role: 'cancel'
          },
          {
            text: 'Удалить',
            role: 'destructive',
            handler: async () => {
              try {
                await eventsService.deleteEvent(id);
                this.router.replace('/admin-events');
              } catch (e) {
                console.error(e);
              }
            }
          }
        ]
      });
      await alert.present();
    }
  }
});
</script>

<style scoped>
.tiers-section {
  margin: 16px 0;
  color: #fff;
}
.tiers-section h3 {
  color: #fff;
  margin-bottom: 12px;
}
.tier-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}
.tier-row ion-input {
  flex: 1;
  min-width: 0;
  --color: #fff;
  --padding-start: 12px;
  --padding-end: 12px;
  --padding-top: 10px;
  --padding-bottom: 10px;
  min-height: 44px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.35);
}
.tier-input-name {
  flex: 1.2;
  text-align: start;
}
.tier-input-name::part(native) {
  text-align: start;
}
.tier-input-amount {
  flex: 0.8;
  max-width: 120px;
  text-align: end;
}
.tier-input-amount::part(native) {
  text-align: end;
}
.tier-row-remove {
  flex-shrink: 0;
  margin: 0;
  --padding-start: 8px;
  --padding-end: 8px;
  min-width: 36px;
  --color: #e85d04;
  color: #e85d04;
}
.tier-row-remove:hover,
.tier-row-remove.ion-activated {
  --color: #f07d34;
  color: #f07d34;
}
.tier-add-btn {
  --border-color: #e85d04;
  --color: #e85d04;
  border-radius: 8px;
  font-weight: 600;
}
.tier-add-btn:hover,
.tier-add-btn.ion-activated {
  --border-color: #f07d34;
  --color: #f07d34;
}
.logo-upload-btn {
  --border-color: #e85d04;
  --color: #e85d04;
  border-radius: 8px;
  font-weight: 600;
}
.logo-upload-btn:hover,
.logo-upload-btn.ion-activated {
  --border-color: #f07d34;
  --color: #f07d34;
}
.save-event-btn {
  --background: #e85d04;
  --background-activated: #dc2f02;
  --background-hover: #f07d34;
  --color: #fff;
  --color-activated: #fff;
  --color-hover: #fff;
  font-weight: 600;
  border-radius: 8px;
}
.event-edit-form {
  padding-bottom: max(32px, env(safe-area-inset-bottom));
}

form ion-item {
  --background: rgba(42, 42, 42, 0.6);
  --color: #fff;
  --border-color: rgba(255, 255, 255, 0.1);
}
form ion-item ion-label[position="stacked"] {
  margin-bottom: 6px;
  flex-shrink: 0;
}
form ion-item ion-input,
form ion-item ion-textarea {
  margin-top: 0;
  min-height: 44px;
}
form ion-item ion-datetime-button.datetime-trigger {
  min-height: 44px;
  --color: #fff;
  --background: transparent;
  color: #fff !important;
  background: transparent !important;
  width: 100%;
}
form ion-item ion-datetime-button.datetime-trigger::part(native) {
  color: #fff !important;
  --background: transparent !important;
  background: transparent !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.35);
  border-radius: 0;
  padding: 10px 0;
  min-height: 44px;
  justify-content: flex-start;
  text-align: start;
  width: 100%;
}
form ion-label,
form ion-input,
form ion-textarea {
  color: #fff !important;
}
.logo-preview {
  width: 64px;
  height: 64px;
  object-fit: cover;
  border-radius: 8px;
  margin-left: 8px;
}
</style>
